import React, { useState, useRef } from "react";
import { FriendRequest } from "../../types";
import { formatDateTime, getUserInitial } from "../../utils/formatting";
import { ToggleSwitch } from "../Settings/ToggleSwitch";

interface FriendRequestItemProps {
  request: FriendRequest;
  onAccept: (
    request: FriendRequest,
    recipientSettings: {
      shareActivities: boolean;
      shareLocation: boolean;
      shareStatus: boolean;
      shareRoutes: boolean;
    },
  ) => void;
  onDecline: (request: FriendRequest) => void;
}

export const FriendRequestItem = React.memo(
  ({ request, onAccept, onDecline }: FriendRequestItemProps) => {
    const [showMenu, setShowMenu] = useState(false);
    const [shareActivities, setShareActivities] = useState(false);
    const [shareLocation, setShareLocation] = useState(false);
    const [shareStatus, setShareStatus] = useState(true);
    const [shareRoutes, setShareRoutes] = useState(false);
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
    const LONG_PRESS_DURATION = 500;
    const DRAG_THRESHOLD = 10;

    const handleTouchStart = (e: React.TouchEvent) => {
      touchStartPosRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };

      longPressTimerRef.current = setTimeout(() => {
        if (touchStartPosRef.current) {
          setShowMenu(!showMenu);
          if ("vibrate" in navigator) {
            navigator.vibrate(50);
          }
        }
      }, LONG_PRESS_DURATION);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      if (touchStartPosRef.current && longPressTimerRef.current) {
        const deltaX = Math.abs(
          e.touches[0].clientX - touchStartPosRef.current.x,
        );
        const deltaY = Math.abs(
          e.touches[0].clientY - touchStartPosRef.current.y,
        );

        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
          touchStartPosRef.current = null;
        }
      }
    };

    const handleTouchEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      touchStartPosRef.current = null;
    };

    return (
      <div
        style={{
          padding: "var(--spacing-md)",
          background: "var(--bg-subtle)",
          borderRadius: "8px",
          marginBottom: "var(--spacing-sm)",
          transition: "all 0.2s",
          position: "relative",
        }}
        onClick={() => setShowMenu(!showMenu)}
        onContextMenu={(e) => {
          e.preventDefault();
          setShowMenu(!showMenu);
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.background = "var(--bg-subtle)")
        }
      >
        {/* Collapse button - positioned absolutely to top right of card */}
        {showMenu && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(false);
            }}
            style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--text-secondary)",
              fontSize: "20px",
              transition: "all 0.2s",
              zIndex: 1,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
              e.currentTarget.style.color = "var(--text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--text-secondary)";
            }}
          >
            ×
          </button>
        )}
        {/* Header row with avatar, info, and Review button */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
        >
          {/* Avatar */}
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: "var(--bg-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              fontWeight: "600",
              color: "var(--text-secondary)",
              flexShrink: 0,
            }}
          >
            {request.fromAvatar ? (
              <img
                src={request.fromAvatar}
                alt={request.fromUsername}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              getUserInitial(request.fromUsername)
            )}
          </div>

          {/* Request info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="setting-label" style={{ lineHeight: "1.2" }}>
              {request.fromUsername}
            </div>
            {request.viaInviteCode && (
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  marginTop: "2px",
                  lineHeight: "1.3",
                }}
              >
                (via invite code)
              </div>
            )}
            {request.fromEmail && !request.viaInviteCode && (
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  marginTop: "2px",
                  lineHeight: "1.3",
                }}
              >
                {request.fromEmail}
              </div>
            )}
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-secondary)",
                marginTop: "2px",
                lineHeight: "1.3",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span>{formatDateTime(request.createdAt)}</span>
              </div>
              {/* Share status indicators - what we will share with them */}
              {(shareActivities || shareLocation || shareStatus) && (
                <span
                  style={{ display: "none", alignItems: "center", gap: "4px" }}
                >
                  {shareActivities && (
                    <span
                      title="Will share activities"
                      style={{ fontSize: "15px", color: "#000", opacity: 1 }}
                    >
                      🚴
                    </span>
                  )}
                  {shareLocation && (
                    <span
                      title="Will share location"
                      style={{ fontSize: "15px", color: "#000", opacity: 1 }}
                    >
                      🗺️
                    </span>
                  )}
                  {shareStatus && (
                    <span
                      title="Will share online status"
                      style={{ fontSize: "15px", color: "#000", opacity: 1 }}
                    >
                      🟢
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Review/Done button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            style={{
              padding: "8px 16px",
              background: showMenu ? "var(--bg-primary)" : "var(--accent)",
              border: "none",
              borderRadius: "6px",
              color: showMenu
                ? "var(--text-secondary)"
                : "var(--text-on-accent)",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "500",
              transition: "all 0.2s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            {showMenu ? "Done" : "Review"}
          </button>
        </div>

        {/* Expanded menu */}
        {showMenu && (
          <div
            style={{
              marginTop: "12px",
              paddingTop: "12px",
              borderTop: "1px solid var(--border)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Share Online Status */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "15px", color: "#000", opacity: 1 }}>
                    🟢
                  </span>
                  <span>Share Online Status</span>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    marginTop: "2px",
                  }}
                >
                  Let them see when you're online
                </div>
              </div>
              <ToggleSwitch checked={shareStatus} onChange={setShareStatus} />
            </div>

            {/* Share Location */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "15px", color: "#000", opacity: 1 }}>
                    🗺️
                  </span>
                  <span>Share Location</span>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    marginTop: "2px",
                  }}
                >
                  Let them see your live location while riding
                </div>
              </div>
              <ToggleSwitch
                checked={shareLocation}
                onChange={setShareLocation}
              />
            </div>

            {/* Share Activities */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "15px", color: "#000", opacity: 1 }}>
                    🚴
                  </span>
                  <span>Share Activities</span>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    marginTop: "2px",
                  }}
                >
                  Let them see your activity summaries
                </div>
              </div>
              <ToggleSwitch
                checked={shareActivities}
                onChange={setShareActivities}
              />
            </div>

            {/* Share Routes */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "var(--text-primary)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "15px", color: "#000", opacity: 1 }}>
                    📍
                  </span>
                  <span>Share Routes</span>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    marginTop: "2px",
                  }}
                >
                  Let them see your saved routes
                </div>
              </div>
              <ToggleSwitch checked={shareRoutes} onChange={setShareRoutes} />
            </div>

            {/* Action buttons */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "12px",
              }}
            >
              <button
                onClick={() => onDecline(request)}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "transparent",
                  border: "1px solid var(--border)",
                  borderRadius: "6px",
                  color: "var(--text-primary)",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                Decline
              </button>
              <button
                onClick={() =>
                  onAccept(request, {
                    shareActivities,
                    shareLocation,
                    shareStatus,
                    shareRoutes,
                  })
                }
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "var(--accent)",
                  border: "none",
                  borderRadius: "6px",
                  color: "var(--text-on-accent)",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                Accept
              </button>
            </div>
          </div>
        )}
      </div>
    );
  },
);
